import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Popper, ClickAwayListener } from '@material-ui/core';
import cn from 'classnames';
import zIndex from '../../foundations/zIndex.module.scss';
import { PopoverArrowIcon } from '../PopoverArrowIcon';
import styles from './PopoverOnHover.module.scss';
PopoverOnHover.defaultProps = {
    placement: 'top',
    timeout: 200,
    openStrategy: 'auto',
    removePadding: false
};
/**
 * Opens a popover that stays open as long as you keep the mouse hovered on the content.
 */ export function PopoverOnHover(props) {
    const { trigger, content, timeout, id, isOpen, placement = 'top', onOpen, onClose, openStrategy, showArrow, removePadding, container } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const timeoutIdRef = useRef(undefined);
    const [open, setOpen] = useState(isOpen || false);
    useEffect(()=>{
        return ()=>{
            clearTimeout(timeoutIdRef.current);
        };
    }, []);
    useEffect(()=>{
        setOpen(isOpen || false);
    }, [
        isOpen
    ]);
    const popperId = useMemo(()=>{
        return id || Math.random().toString();
    }, [
        id
    ]);
    const schedulePopoverHiding = ()=>{
        timeoutIdRef.current = setTimeout(()=>{
            setOpen(false);
            onClose && onClose();
        }, timeout);
    };
    const triggerPropsCommon = {
        ref: (el)=>setAnchorEl(el),
        ['aria-owns']: open ? popperId : undefined,
        ['aria-haspopup']: 'true',
        ['data-popover-is-open']: open ? 'true' : 'false'
    };
    const triggerPropsHoverStrategy = _object_spread({
        onMouseEnter: ()=>{
            setOpen(true);
            clearTimeout(timeoutIdRef.current);
            onOpen && onOpen();
        },
        onMouseLeave: schedulePopoverHiding
    }, triggerPropsCommon);
    const triggerPropsClickStrategy = _object_spread({
        onClick: ()=>{
            setOpen(!open);
            if (open) {
                onClose && onClose();
            } else {
                onOpen && onOpen();
            }
        }
    }, triggerPropsCommon);
    const targetContainerPropsHoverStrategy = {
        onClick: (e)=>e.stopPropagation(),
        onMouseEnter: ()=>{
            clearTimeout(timeoutIdRef.current);
        },
        onMouseLeave: schedulePopoverHiding
    };
    const targetContainerPropsClickStrategy = {
        onClick: (event)=>event.stopPropagation()
    };
    const targetContainerPropsAutoStrategy = {
        onClick: (event)=>event.stopPropagation()
    };
    let triggerProps = {}, targetContainerProps = {};
    switch(openStrategy){
        case 'auto':
            if (isOpen === undefined) {
                // if no param is passed => change to hover strategy
                triggerProps = triggerPropsHoverStrategy;
                targetContainerProps = targetContainerPropsHoverStrategy;
            } else {
                triggerProps = triggerPropsCommon;
                targetContainerProps = targetContainerPropsAutoStrategy;
            }
            break;
        case 'hover':
            triggerProps = triggerPropsHoverStrategy;
            targetContainerProps = targetContainerPropsHoverStrategy;
            break;
        case 'click':
            triggerProps = triggerPropsClickStrategy;
            targetContainerProps = targetContainerPropsClickStrategy;
            break;
    }
    const contentProps = {
        closePopover: ()=>setOpen(false)
    };
    const placementClass = useMemo(()=>{
        if (placement.startsWith('top')) {
            return 'top';
        } else if (placement.startsWith('bottom')) {
            return 'bottom';
        } else if (placement.startsWith('left')) {
            return 'left';
        } else {
            return 'right';
        }
    }, [
        placement
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            trigger(triggerProps),
            /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({}, targetContainerProps), {
                children: anchorEl && /*#__PURE__*/ _jsxs(Popper, {
                    style: {
                        zIndex: zIndex.popover
                    },
                    id: popperId,
                    open: open,
                    anchorEl: anchorEl,
                    placement: placement,
                    className: styles[placementClass],
                    container: container,
                    modifiers: {
                        offset: {
                            // Distance from the trigger element.
                            // See: https://popper.js.org/docs/v1/#modifiers..offset
                            offset: '0, 8'
                        }
                    },
                    children: [
                        showArrow && /*#__PURE__*/ _jsxs("span", {
                            className: cn(styles.arrow),
                            "x-arrow": "true",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: styles.arrowShadow
                                }),
                                /*#__PURE__*/ _jsx(PopoverArrowIcon, {
                                    className: styles.arrowCover
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(ClickAwayListener, {
                            onClickAway: ()=>openStrategy !== 'auto' && setOpen(false),
                            children: /*#__PURE__*/ _jsx("div", {
                                className: cn(styles.popoverContent, {
                                    [styles.withArrow]: showArrow,
                                    [styles.builtInPadding]: !removePadding
                                }),
                                children: typeof content === 'function' ? content(contentProps) : content
                            })
                        })
                    ]
                })
            }))
        ]
    });
}
